// M12.2 — Shared date-range helpers used by the client, print-html, and
// digest-html so the label a user sees on the dashboard matches the PDF,
// the digest email, and the shared viewer byte-for-byte.
//
// All formatting happens in the browser/viewer locale via Intl — the
// underlying date math is pure UTC parsing (no timezone shift), so a
// range like 2026-04-12 → 2026-04-18 renders identically regardless of
// where the consumer runs.

export type ReportRange = "last_7d" | "last_28d" | "last_90d";
export type ReportMetaRange = ReportRange | "custom";
// M12.12 — "custom" joined the set so a user-picked reference window
// round-trips through every surface that formats a date-range label.
export type ReportCompare = "previous" | "year_over_year" | "none" | "custom";

/** Day-count of an inclusive ISO-date range. */
export function dayCount(startDate: string, endDate: string): number {
  const start = parseIso(startDate);
  const end = parseIso(endDate);
  const ms = end.getTime() - start.getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000)) + 1;
}

/**
 * Human-friendly label for an inclusive ISO-date window. Day-first
 * (DD Mon) ordering matches the rest of the dashboard tables (DepthSection,
 * time-series tooltips). Year is omitted when both dates fall in the
 * current calendar year and included on BOTH ends otherwise, so
 * past-year and cross-year ranges are unambiguous without the reader
 * having to infer it.
 *
 * Both ends in current year → "22 Mar – 18 Apr"
 * Past year (same year)     → "22 Mar 2025 – 18 Apr 2025"
 * Cross year                → "22 Dec 2024 – 18 Jan 2025"
 *
 * `nowMs` is an injectable "today" — defaults to `Date.now()`. Kept
 * optional so existing callers don't break; the digest/PDF builders
 * can pass the send-time if they want the label to describe "the
 * week we sent this report" even if it's opened later.
 */
export function formatDateRangeLabel(
  startDate: string,
  endDate: string,
  nowMs: number = Date.now(),
): string {
  const start = parseIso(startDate);
  const end = parseIso(endDate);
  const currentYear = new Date(nowMs).getUTCFullYear();
  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();
  // Drop the year only when BOTH ends are in the current year — any
  // drift into a past year (or forward cross-year) shows years on
  // both sides so the reader never has to guess which end belongs
  // to which year.
  const omitYear = startYear === currentYear && endYear === currentYear;
  if (omitYear) {
    return `${formatDayMonth(start)} – ${formatDayMonth(end)}`;
  }
  return `${formatDayMonth(start)} ${startYear} – ${formatDayMonth(end)} ${endYear}`;
}

/**
 * Subtitle-friendly sentence describing the comparison period, e.g.
 *   "compared to previous 28 days"
 *   "compared to the same window a year ago"
 * Returns null when compare === "none" or required bounds are missing.
 */
export function compareLabel(
  compare: ReportCompare,
  periodStart: string,
  periodEnd: string,
  comparePeriodStart: string | null,
  comparePeriodEnd?: string | null,
): string | null {
  if (compare === "none") return null;
  if (!comparePeriodStart) return null;
  if (compare === "year_over_year") {
    return "compared to the same window a year ago";
  }
  if (compare === "custom") {
    // User-picked window — label by the actual dates rather than a
    // computed "previous N days" count, which wouldn't match what the
    // user sees in the Comparing-to cell.
    if (comparePeriodEnd) {
      return `compared to ${formatDateRangeLabel(comparePeriodStart, comparePeriodEnd)}`;
    }
    return "compared to a custom window";
  }
  // "previous" — call it out with the day count so the user knows what the
  // compare window is when they're on a custom range.
  const days = dayCount(periodStart, periodEnd);
  return `compared to previous ${days} days`;
}

/**
 * Single-line subtitle combining the date range and compare clause.
 *   "Mar 22 – Apr 18 2026 · compared to previous 28 days"
 */
export function rangeSubtitle(
  startDate: string,
  endDate: string,
  compare: ReportCompare,
  comparePeriodStart: string | null,
  comparePeriodEnd?: string | null,
): string {
  const base = formatDateRangeLabel(startDate, endDate);
  const compareClause = compareLabel(
    compare,
    startDate,
    endDate,
    comparePeriodStart,
    comparePeriodEnd,
  );
  return compareClause ? `${base} · ${compareClause}` : base;
}

function parseIso(s: string): Date {
  const [y, m, d] = s.split("-").map((p) => parseInt(p, 10));
  return new Date(Date.UTC(y, m - 1, d));
}

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatDayMonth(d: Date): string {
  return `${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]}`;
}

/**
 * Short table-row date label. Prefixes the short weekday and drops
 * the year when the date falls in the current calendar year — used by
 * DepthSection, time-series tables, etc. where the report window is
 * already communicated in the section header.
 *
 * Current year: "Mon 23 Mar"
 * Past/future year: "Mon 23 Mar 2025"
 */
export function formatTableRowDate(isoDate: string, nowMs: number = Date.now()): string {
  const d = parseIso(isoDate);
  const currentYear = new Date(nowMs).getUTCFullYear();
  const dayOfWeek = WEEKDAYS_SHORT[d.getUTCDay()];
  const base = `${dayOfWeek} ${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]}`;
  return d.getUTCFullYear() === currentYear ? base : `${base} ${d.getUTCFullYear()}`;
}
